import './index.css';
import { Link } from 'react-router-dom';
import ServicesSubMenu from '../ServicesSubMenu';

const rates = [
  { service: 'Cloud Support', rate: '$125/hr' },
  { service: 'Software Development', rate: '$150/hr' },
  { service: 'Cloud Architecture', rate: '$175/hr' },
  { service: 'AI Architecture and Consulting', rate: '$175-$200/hr' },
  { service: 'Emergency or After-hours Support', rate: '$225/hr' },
  { service: 'Micro-controller and Electronics', rate: '$150/hr' },
  { service: 'Tutoring', rate: '$50/hr' },
];

const pricingSamples = [
  { 
    service: 'Email Service', 
    price_range: '$500-$700', 
    description: 'Establish professional credibility with custom domain email addresses. Move beyond Gmail or Yahoo to strengthen your brand identity and ensure all client communications carry your domain name.' 
  },
  { 
    service: 'Calendar Service', 
    price_range: '$900-$1,200', 
    description: 'Streamline appointment scheduling with an integrated calendar system. Display real-time availability and let clients book directly on your site—eliminating back-and-forth emails and missed opportunities.' 
  },
  { 
    service: 'Membership Login', 
    price_range: '$1,500-$2,500', 
    description: 'Build a complete member ecosystem with secure login and account management. Enable membership tiers, cancellations, and seamless integration with shopping, scheduling, and other services to maximize customer lifetime value.' 
  },
  { 
    service: 'Opt-Out Service', 
    price_range: '$800', 
    description: 'Stay GDPR and CAN-SPAM compliant with built-in opt-out tracking. Manage unsubscribe requests in a secure database, access admin dashboards, and maintain audit-ready records to protect your business from regulatory issues.' 
  },
  { 
    service: 'Shopping Cart', 
    price_range: '$2,000-$3,500', 
    description: 'Power online sales with a fully integrated shopping cart. Remember customer preferences and order history, process secure payments, and sync seamlessly with membership and scheduling systems to drive revenue growth.' 
  },
  { 
    service: 'AI Chatbot', 
    price_range: '$800-$2,000', 
    description: 'Provide instant customer support with an AI chatbot. Start with smart FAQs and conversational responses, or scale to a full RAG system that learns from your documents and grows with your business needs.' 
  },
  { 
    service: 'SMS & MMS Integration', 
    price_range: '$2,000-$6,000', 
    description: 'Connect with customers where they are most responsive—their phones. We handle provider registration and connect SMS and MMS directly to your chatbot, FAQs, and customer communications. Built-in compliance and opt-out management ensures you stay protected while building meaningful two-way conversations.' 
  },
]

const pricingNotes = [
  'Simple fixed-price projects may still be available when the scope is clear.',
  'Third-party costs such as domains, hosting, SMS, APIs, app store fees, subscriptions, and payment processing are separate unless included in a written estimate.',
  'Emergency and after-hours support is priced higher because it interrupts normal scheduling outside 8:00 AM to 4:00 PM Central Time.',
  'If a service overlaps categories, Hogcloud will confirm the expected rate before work begins.',
];

const Pricing = () => (
  <div className="pricing-page">
    <ServicesSubMenu />

    <section className="pricing-hero">
      <p className="pricing-kicker">Hogcloud Pricing</p>
      <h1>Professional Technology Rates for Northwest Arkansas Businesses</h1>
      <p>
        Hogcloud is owned by Matt Chandler, a Fayetteville, Arkansas software
        architect and engineer with more than 25 years of experience in software
        services, AWS cloud architecture, full-stack development, business systems,
        AI solutions, electronics, and maker education.
      </p>
      <p>
        Matt&apos;s credentials include AWS Certified Developer - Associate, AWS
        Certified Solutions Architect - Associate, and AWS Academy Cloud Foundations.
      </p>
    </section>

    <section className="pricing-section" aria-labelledby="pricing-rates-title">
      <div className="pricing-section-header">
        <p className="pricing-kicker">Hourly Rates</p>
        <h2 id="pricing-rates-title">Service rates</h2>
      </div>

      <div className="pricing-table-wrap">
        <table className="pricing-rate-table">
          <thead>
            <tr>
              <th scope="col">Service</th>
              <th scope="col">Rate</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={rate.service}>
                <th scope="row">{rate.service}</th>
                <td>{rate.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="pricing-retainers" aria-labelledby="pricing-retainers-title">
      <div>
        <p className="pricing-kicker">Ongoing Support</p>
        <h2 id="pricing-retainers-title">Retainers start at $400-$800/month</h2>
        <p>
          Retainers are available for customers who need predictable ongoing help
          with updates, support, cloud guidance, chatbot documents, website changes,
          technical planning, or small recurring tasks.
        </p>
      </div>
      <Link className="pricing-primary-link" to="/contact">Ask About a Retainer</Link>
    </section>
    <section className="pricing-section" aria-labelledby="pricing-notes-title">
      <div className="pricing-section-header">
        <h2 id="pricing-notes-title">Project Pricing Samples</h2>
      </div>
      <em>Here are a few price ranges to expect for our services, all of which can fluxuate based on the integration requirements and project scope.</em>
      <div className="pricing-note-grid">
        {pricingSamples.map((note) => (
          <div key={note.service}>
            <h3>{note.service}</h3>
            <p className="price">{note.price_range}</p>
            <p className="description">{note.description}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="pricing-section" aria-labelledby="pricing-notes-title">
      <div className="pricing-section-header">
        <p className="pricing-kicker">What to Expect</p>
        <h2 id="pricing-notes-title">Pricing notes</h2>
      </div>

      <div className="pricing-note-grid">
        {pricingNotes.map((note) => (
          <article key={note}>
            <p>{note}</p>
          </article>
        ))}
      </div>
    </section>




    <section className="pricing-contact" aria-labelledby="pricing-contact-title">
      <h2 id="pricing-contact-title">Ready to scope the work?</h2>
      <p>
        Send a short description of the project, timeline, budget range if known,
        and any examples or account details that help Hogcloud estimate accurately.
      </p>
     <p>Hogcloud is built by engineers with years of enterprise-level experience. We don't rely on generic plugins or off-the-shelf solutions—we architect custom systems tailored to your business needs. Our deep technical expertise allows us to deliver sophisticated, production-ready services at prices that reflect our operational efficiency, not our value. When you choose Hogcloud, you're investing in proven engineering excellence backed by real credentials and a track record of successful implementations.
      </p>
      <br></br><Link className="pricing-primary-link" to="/contact">Contact Hogcloud</Link>
    </section>
  </div>
);

export default Pricing;
